import type { Injector } from '@angular/core';
import {
  createBulkEditSectionsToken,
  FIELD_PERMISSION_CHECKER,
  type BulkEditSection,
  type BulkPatchFragment,
  type FieldPermissionChecker,
} from '@zag/ui';

function hasPermission(permission: string): (injector: Injector) => boolean {
  return injector => injector.get<FieldPermissionChecker>(FIELD_PERMISSION_CHECKER).hasPermission(permission);
}
import { RoleAssignableBulkSectionComponent } from './role-assignable-bulk-section/role-assignable-bulk-section.component';
import { MembersBulkSectionComponent } from './members-bulk-section/members-bulk-section.component';
import { PermissionsBulkSectionComponent } from './permissions-bulk-section/permissions-bulk-section.component';

export const ROLES_BULK_EDIT_SECTIONS_TOKEN = createBulkEditSectionsToken('roles');

interface AssignableState {
  assignableToPositions: boolean;
}

interface MembersState {
  userIds: string[];
}

interface PermissionsState {
  permissionIds: string[];
}

export const ROLES_BULK_EDIT_SECTIONS: BulkEditSection[] = [
  {
    id: 'bulk-role-assignable',
    icon: 'account_tree',
    titleKey: 'bulkEdit.roles.assignable.title',
    priority: 10,
    component: RoleAssignableBulkSectionComponent,
    previewLabelKey: 'bulkEdit.roles.assignable.preview',
    appliesToArrayField: false,
    supportsClear: false,
    buildPatchFragment(state: unknown): BulkPatchFragment {
      const s = state as AssignableState;
      return {
        patch: {
          assignableToPositions: { op: 'set', value: s?.assignableToPositions ?? false },
        },
      };
    },
    visibilityCheck: hasPermission('rbac:roles:read'),
    editCheck: hasPermission('rbac:roles:update'),
  },
  {
    id: 'bulk-members',
    icon: 'person_add',
    titleKey: 'bulkEdit.roles.members.title',
    priority: 20,
    component: MembersBulkSectionComponent,
    previewLabelKey: 'bulkEdit.roles.members.preview',
    appliesToArrayField: true,
    supportsClear: false,
    buildPatchFragment(state: unknown): BulkPatchFragment {
      const s = state as MembersState;
      if (!s?.userIds?.length) return {};
      return { arrayOps: { members: { op: 'append', items: s.userIds.map(userId => ({ userId })) } } };
    },
    visibilityCheck: hasPermission('rbac:users:read'),
    editCheck: hasPermission('rbac:roles:update'),
  },
  {
    id: 'bulk-permissions',
    icon: 'key',
    titleKey: 'bulkEdit.roles.permissions.title',
    priority: 30,
    component: PermissionsBulkSectionComponent,
    previewLabelKey: 'bulkEdit.roles.permissions.preview',
    appliesToArrayField: true,
    supportsClear: false,
    buildPatchFragment(state: unknown): BulkPatchFragment {
      const s = state as PermissionsState;
      if (!s?.permissionIds?.length) return {};
      return { arrayOps: { permissions: { op: 'append', items: s.permissionIds.map(permissionId => ({ permissionId })) } } };
    },
    visibilityCheck: hasPermission('rbac:permissions:read'),
    editCheck: hasPermission('rbac:roles:update'),
  },
];
